// Improved BranchClientViewHelper.ts (TypeScript)

class EventEmitter<T = any> {
    private listeners: Record<string, ((data: T) => void)[]> = {};
  
    on(event: string, listener: (data: T) => void): void {
      this.listeners[event] = this.listeners[event] || [];
      if (!this.listeners[event].includes(listener)) {
        this.listeners[event].push(listener);
      }
    }
  
    off(event: string, listener: (data: T) => void): boolean {
      const list = this.listeners[event] || [];
      const index = list.indexOf(listener);
      if (index !== -1) {
        list.splice(index, 1);
        return true;
      }
      return false;
    }
  
    emit(event: string, data: T): void {
      const list = this.listeners[event];
      if (list) {
        list.forEach((listener) => {
          try {
            listener(data);
          } catch (e) {
            console.warn("Error dispatching event:", event, e);
          }
        });
      }
    }
  }
  
  export interface CallState {
    name: string;
    value: any;
  }
  
  export class BranchClientViewHelper {
    static EVENTS = {
      DISPLAY_STATE_CHANGE: "bcDisplayStateChange",
      SERVICE_STATE_CHANGE: "bcServiceStateChange",
      CALL_STATE_CHANGE: "bcUserStateChange",
      ERROR: "bcError",
    } as const;
  
    private eventEmitter = new EventEmitter<any>();
    private subscriptions: (() => void)[] = [];
    private conversation: any = null;
    private branchClientApi: any;
    private currentDisplayState = { visible: undefined as boolean | undefined };
    private currentServiceState = { agentsReady: -1, agentsOnline: -1 };
    private currentCallState: CallState = { name: "", value: null };
    
    // Initialize properties with empty functions
    private internalCallEventHandler: (event: any) => void = () => {};
    private internalQueueEventHandler: (event: any) => void = () => {};
    private internalConversationEventHandler: {
      close: () => void;
      conversationStateChange: (event: any) => Promise<void>;
    } = {
      close: () => {},
      conversationStateChange: async () => {}
    };
    private internalBranchClientApiHandler: {
      deinitializing: () => void;
      activeConversationChange: (conversation: any) => void;
      availableNamedAreasChange: (areas: any) => void;
      agentAvailabilitiesChange: (areas: any) => void;
    } = {
      deinitializing: () => {},
      activeConversationChange: () => {},
      availableNamedAreasChange: () => {},
      agentAvailabilitiesChange: () => {}
    };
    private availabilityEventName: "agentAvailabilitiesChange" | "availableNamedAreasChange" = "availableNamedAreasChange";
  
    constructor(branchClientApi: any) {
      this.branchClientApi = branchClientApi;
      this.bindInternalHandlers();
      console.debug("BranchClientViewHelper initialized");
    }
  
    private bindInternalHandlers() {
      this.internalCallEventHandler = (event: any) => {
        if (event === "ACTIVE_JOINED") {
          this.setNewCallState({ name: "CONNECTED", value: null });
        }
      };
  
      this.internalQueueEventHandler = (event: any) => {
        this.setNewCallState({
          name: "QUEUED",
          value: {
            position: event.position,
            averageWaitingTime: event.averageWaitingTime,
          },
        });
      };
  
      this.internalConversationEventHandler = {
        close: () => this.setNewCallState({ name: "INIT", value: null }),
        conversationStateChange: async (event: any) => {
          if (!this.conversation) return;
  
          if (event === "QUEUED") {
            this.conversation.on("queueStatusChange", this.internalQueueEventHandler);
            const queueStatus = await this.conversation.getQueueStatus();
            this.internalQueueEventHandler(queueStatus);
          } else if (event === "ACTIVE") {
            this.setNewCallState({ name: "CONNECTING", value: null });
            this.conversation.on("callStateChange", this.internalCallEventHandler);
            const callState = await this.conversation.getCallState();
            this.internalCallEventHandler(callState);
          } else {
            this.setNewCallState({ name: "INIT", value: null });
          }
  
          if (event !== "ACTIVE") {
            this.conversation.off("callStateChange", this.internalCallEventHandler);
          }
          if (event !== "QUEUED") {
            this.conversation.off("queueStatusChange", this.internalQueueEventHandler);
          }
        },
      };
  
      this.internalBranchClientApiHandler = {
        deinitializing: () => this.destroy(),
        activeConversationChange: (conversation: any) => this.setupConversation(conversation),
        availableNamedAreasChange: (areas: any) => this.setNewServiceState(areas),
        agentAvailabilitiesChange: (areas: any) => this.setNewServiceState(areas),
      };

      this.on("deinitializing", this.internalBranchClientApiHandler.deinitializing);
      this.on("activeConversationChange", this.internalBranchClientApiHandler.activeConversationChange);

      // Compatibility strategy: subscribe one event only (new first, legacy fallback).
      this.availabilityEventName =
        typeof this.branchClientApi.getAgentAvailabilities === "function"
          ? "agentAvailabilitiesChange"
          : "availableNamedAreasChange";

      try {
        this.on(this.availabilityEventName, this.internalBranchClientApiHandler[this.availabilityEventName]);
      } catch {
        this.availabilityEventName =
          this.availabilityEventName === "agentAvailabilitiesChange"
            ? "availableNamedAreasChange"
            : "agentAvailabilitiesChange";
        this.on(this.availabilityEventName, this.internalBranchClientApiHandler[this.availabilityEventName]);
      }
    }
  
    maintain(subscription: () => void) {
      this.subscriptions.push(subscription);
    }
  
    on(event: string, listener: (data: any) => void): void {
      if (Object.values(BranchClientViewHelper.EVENTS).includes(event as any)) {
        this.eventEmitter.on(event, listener);
        this.maintain(() => this.eventEmitter.off(event, listener));
      } else {
        this.branchClientApi.on(event, listener);
        this.maintain(() => this.branchClientApi.off(event, listener));
      }
    }
  
    private setupConversation(conversation: any) {
      if (this.conversation && !this.conversation.isDestroyed?.()) {
        for (const event in this.internalConversationEventHandler) {
          this.conversation.off(event, this.internalConversationEventHandler[event as keyof typeof this.internalConversationEventHandler]);
        }
      }
  
      this.conversation = conversation;
  
      if (this.conversation) {
        for (const event in this.internalConversationEventHandler) {
          this.conversation.on(event, this.internalConversationEventHandler[event as keyof typeof this.internalConversationEventHandler]);
        }
      }
    }
  
    private setNewCallState(callState: CallState) {
      if (
        callState.name !== this.currentCallState.name ||
        callState.value !== this.currentCallState.value
      ) {
        const oldCallState = { ...this.currentCallState };
        const newCallState = { ...callState };
        this.currentCallState = newCallState;
  
        this.eventEmitter.emit(BranchClientViewHelper.EVENTS.CALL_STATE_CHANGE, {
          oldCallState,
          newCallState,
        });
  
        this.setNewDisplayState(newCallState.name === "CONNECTED");
      }
    }
  
    private setNewDisplayState(visible: boolean) {
      if (this.currentDisplayState.visible !== visible) {
        this.currentDisplayState.visible = !!visible;
        this.eventEmitter.emit(BranchClientViewHelper.EVENTS.DISPLAY_STATE_CHANGE, {
          ...this.currentDisplayState,
        });
      }
    }
  
    private setNewServiceState(serviceState: any) {
      this.currentServiceState = serviceState;
      // Directly emit the serviceState, not an array of it.
      this.eventEmitter.emit(
        BranchClientViewHelper.EVENTS.SERVICE_STATE_CHANGE,
        this.currentServiceState // Directly pass the serviceState
      );
    }
  
    destroy() {
      this.subscriptions.forEach((sub) => sub());
      this.subscriptions = [];
      this.setupConversation(null);
    }
  
    async init(): Promise<void> {
      const activeConversation = await this.branchClientApi.getActiveConversation().catch(() => null);
      this.setupConversation(activeConversation);
  
      if (this.conversation) {
        const conversationState = await this.conversation.getConversationState();
        this.internalConversationEventHandler.conversationStateChange(conversationState);
      } else {
        this.setNewCallState({ name: "INIT", value: null });
      }
  
      // Method compatibility: prefer new API, fallback to legacy on missing/failed call.
      let serviceState: any = [];
      let resolvedFromNewApi = false;
      if (typeof this.branchClientApi.getAgentAvailabilities === "function") {
        try {
          serviceState = await this.branchClientApi.getAgentAvailabilities();
          resolvedFromNewApi = true;
        } catch {
          resolvedFromNewApi = false;
        }
      }
      if (!resolvedFromNewApi && typeof this.branchClientApi.getAvailableNamedAreas === "function") {
        try {
          serviceState = await this.branchClientApi.getAvailableNamedAreas();
        } catch {
          serviceState = [];
        }
      }
      this.internalBranchClientApiHandler[this.availabilityEventName](serviceState);
      console.debug(`[BranchClientViewHelper] init completed`);
    }
  }
  
  export const getQueryParam = (param: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  };
  
  export const getUnbluBranchClientAppElement = (): HTMLUnbluBranchClientAppElement | null => {
    return document.querySelector("unblu-branch-client-app");
  };
  
  export const applyConfiguration = (
    appElement: HTMLElement,
    config: {
      apiKey?: string;
      branchClientToken?: string;
      serverUrl?: string;
      entryPath?: string;
      namedArea?: string;
      locale?: string;
      autoInit?: boolean;
    }
  ): void => {
    appElement.setAttribute("api-key", config.apiKey || "");
    appElement.setAttribute("branch-client-token", config.branchClientToken || "");
    appElement.setAttribute("server-url", config.serverUrl || "");
    appElement.setAttribute("entry-path", config.entryPath || "");
    appElement.setAttribute("named-area", config.namedArea || "");
    appElement.setAttribute("locale", config.locale || "");
    appElement.setAttribute("auto-init", config.autoInit === false ? "false" : "true");
  };
  